// src/components/generic/ImageCropEditor/ImageCropEditor.tsx
//
// Mobile-first image editor: crop, rotate, flip, and remove background.
//
// Two crop interaction models, chosen by the selected aspect ratio:
//
// - FIXED ratio (1:1, 16:9, portrait, …): the crop frame is FIXED in the
//   centre of the viewport and the user drags / pinch-zooms the IMAGE behind
//   it. Great for "fill this exact shape".
//
// - FREE: the image is FROZEN (fit fully into the viewport) and the user drags
//   a resizable / movable crop rectangle over it — the familiar desktop crop
//   model. This is what "Free" means and it's why moving the image made no
//   sense there before.
//
// Architecture: rotation, flip and background-removal are BAKED into a working
// canvas whenever they change. That leaves only pan+zoom (fixed) or the frame
// rect (free) as live geometry, so the crop math stays simple and identical
// regardless of how the image was rotated / flipped.
//
// Pure canvas output. Background removal lazy-loads @imgly/background-removal.

import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Box, Group, Text, ActionIcon, Button, Portal, Loader, Tooltip, Transition } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { fetchWithLimit } from "@em/api";

import { AppIcon } from "../AppIcon";
import { formatFileSize } from "../fileHelpers";
import { OWN_ESCAPE_ATTR, useOverlayStack } from "../overlayStack";

import { removeBackground, compositeOnColor } from "./backgroundRemoval";
import { encodeMimeFor, imageHasAlpha, readContextPixels, TRANSPARENCY_CHECKER } from "./imageAlpha";

// ============================================
// Types
// ============================================

export type CropAspect =
  | "free"
  | "square"
  | "4:3"
  | "3:4"
  | "16:9"
  | "9:16"
  | "2:1"
  | "3:2"
  | "4:5"
  | "2:3"
  | "letter"
  | "a4"
  | number;

export interface ImageCropEditorProps {
  opened: boolean;
  onClose: () => void;
  /** Called with the edited image blob (jpeg, or png when it carries transparency). */
  onConfirm: (blob: Blob) => void;
  /** Called when the user taps "Skip" — returns original blob unchanged. */
  onSkip?: (blob: Blob) => void;
  /** The source image to edit (Blob or object URL). */
  image: Blob | string | null;
  /** Aspect ratio constraint. A number locks the ratio and hides the selector. Default "free". */
  aspectRatio?: CropAspect;
  /** Whether to show the aspect ratio selector. Default true. */
  showAspectRatioSelector?: boolean;
  /** Whether to show the "Skip crop" button. Default true. */
  showSkip?: boolean;
  /** Whether to show rotate + flip controls. Default true. */
  showRotation?: boolean;
  /** Whether to offer background removal. Default true. */
  showBackgroundRemoval?: boolean;
  /** Max output dimension (width or height). Default 1920. */
  maxOutputDimension?: number;
  /** JPEG quality for output (0–1). Default 0.9. */
  outputQuality?: number;
  /** Title shown at the top. Default "Edit Image". */
  title?: string;
  /**
   * A2e #91 — offer ONLY these presets (their `value`s, e.g. `["2:1", "16:9", "3:2", "4:3", "square"]` for a logo that may
   * be square or up to twice as wide). `aspectRatio` should be one of them. Default: every preset.
   */
  aspectChoices?: string[];
}

// ============================================
// Aspect Ratio Presets
// ============================================

interface AspectPreset {
  value: string;
  label: string;
  ratio: number | null; // null = free
}

// Ordered: free, square, then landscape (wide→narrow), then portrait (wide→narrow). #79 — the two poster sizes hosts
// actually upload (owner, 2026-10-02: a letter-size poster), so cropping one to its page is one tap, not a guess.
const ASPECT_PRESETS: AspectPreset[] = [
  { value: "free", label: "Free", ratio: null },
  { value: "square", label: "1:1", ratio: 1 },
  { value: "2:1", label: "2:1", ratio: 2 },
  { value: "16:9", label: "16:9", ratio: 16 / 9 },
  { value: "3:2", label: "3:2", ratio: 3 / 2 },
  { value: "4:3", label: "4:3", ratio: 4 / 3 },
  { value: "4:5", label: "4:5", ratio: 4 / 5 },
  { value: "letter", label: "Letter", ratio: 8.5 / 11 },
  { value: "3:4", label: "3:4", ratio: 3 / 4 },
  { value: "a4", label: "A4", ratio: 210 / 297 },
  { value: "2:3", label: "2:3", ratio: 2 / 3 },
  { value: "9:16", label: "9:16", ratio: 9 / 16 },
];

const parseAspectRatio = (value: CropAspect): number | null => {
  if (typeof value === "number") {
    return value;
  }
  const preset = ASPECT_PRESETS.find((p) => p.value === value);
  return preset?.ratio ?? null;
};

// Background fill choices after removal.
const BG_FILL_COLORS: { key: string; label: string; value: string | null; swatch: string }[] = [
  { key: "transparent", label: "Transparent", value: null, swatch: "checker" },
  { key: "white", label: "White", value: "#FFFFFF", swatch: "#FFFFFF" },
  { key: "lightgray", label: "Light gray", value: "#F1F3F5", swatch: "#F1F3F5" },
  { key: "black", label: "Black", value: "#111111", swatch: "#111111" },
  { key: "blue", label: "Blue", value: "#2196F3", swatch: "#2196F3" },
  { key: "green", label: "Green", value: "#4CAF50", swatch: "#4CAF50" },
];

// ============================================
// Constants
// ============================================

const DEFAULT_MAX_OUTPUT = 1920;
const DEFAULT_QUALITY = 0.9;
const MAX_ZOOM_MULTIPLIER = 5;
const FRAME_PADDING = 12; // free-mode contain-fit margin (leaves room for the resize handles)
const CORNER_LEN = 20;
const CORNER_STROKE = 3;
const GRID_FADE_DELAY_MS = 600;
const MIN_FRAME_PX = 48; // minimum free-crop rectangle size

// Rendered on a NON-transformed layer so it never scales with zoom.
const CHECKER_BG: React.CSSProperties = TRANSPARENCY_CHECKER;

// The longest side the transparency check reads at (a bigger picture is read scaled down: still finds any transparent
// area wider than a pixel or two, without holding a 48 MB pixel buffer for a phone photo).
const ALPHA_CHECK_MAX_SIDE = 2048;

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** "16:9" for tidy ratios, else "1.78:1". */
function ratioLabel(w: number, h: number): string {
  const rw = Math.round(w);
  const rh = Math.round(h);
  if (!rw || !rh) {
    return "";
  }
  const g = gcd(rw, rh) || 1;
  const a = rw / g;
  const b = rh / g;
  if (a <= 64 && b <= 64) {
    return `${a}:${b}`;
  }
  return `${(w / h).toFixed(2)}:1`;
}

// ============================================
// Geometry Helpers
// ============================================

/**
 * Crop frame dimensions fitting inside the available area (fixed-ratio mode).
 * pad=0 → the frame is flush to the viewport on its constraining axis (like the
 * native croppers), so a preselected ratio isn't a small floating rectangle.
 */
function computeCropFrame(areaW: number, areaH: number, ratio: number | null, pad = 0): { width: number; height: number } {
  const maxW = areaW - pad * 2;
  const maxH = areaH - pad * 2;

  if (maxW <= 0 || maxH <= 0) {
    return { width: 100, height: 100 };
  }

  if (ratio == null) {
    const freeRatio = areaW > areaH ? 4 / 3 : 3 / 4;
    let w = maxW;
    let h = w / freeRatio;
    if (h > maxH) {
      h = maxH;
      w = h * freeRatio;
    }
    return { width: Math.round(w), height: Math.round(h) };
  }

  let w = maxW;
  let h = w / ratio;
  if (h > maxH) {
    h = maxH;
    w = h * ratio;
  }
  return { width: Math.round(w), height: Math.round(h) };
}

/** Contain-fit an image into the viewport (free mode). Returns the displayed rect. */
function computeContainFit(
  vpW: number,
  vpH: number,
  imgW: number,
  imgH: number
): { left: number; top: number; width: number; height: number; scale: number } {
  const avW = Math.max(1, vpW - FRAME_PADDING * 2);
  const avH = Math.max(1, vpH - FRAME_PADDING * 2);
  const scale = Math.min(avW / imgW, avH / imgH);
  const width = imgW * scale;
  const height = imgH * scale;
  return { left: (vpW - width) / 2, top: (vpH - height) / 2, width, height, scale };
}

/** Cover min-scale so the image fully covers the crop frame (fixed mode). */
function computeMinScale(imgW: number, imgH: number, frameW: number, frameH: number): number {
  return Math.max(frameW / imgW, frameH / imgH);
}

/** Clamp pan so the image always covers the crop frame (fixed mode). */
function clampOffset(
  ox: number,
  oy: number,
  imgW: number,
  imgH: number,
  frameW: number,
  frameH: number,
  scale: number
): { x: number; y: number } {
  const sW = imgW * scale;
  const sH = imgH * scale;
  const maxX = Math.max(0, (sW - frameW) / 2);
  const maxY = Math.max(0, (sH - frameH) / 2);
  return { x: clamp(ox, -maxX, maxX), y: clamp(oy, -maxY, maxY) };
}

function touchDist(t1: React.Touch, t2: React.Touch): number {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

// ============================================
// Bake rotation + flip into a working canvas
// ============================================

function bakeImage(
  img: HTMLImageElement,
  rotation: number,
  flipH: boolean,
  flipV: boolean
): { canvas: HTMLCanvasElement; url: string; w: number; h: number } {
  const isRot = rotation % 180 !== 0;
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const cw = isRot ? h : w;
  const ch = isRot ? w : h;

  const canvas = document.createElement("canvas");
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d")!;
  ctx.translate(cw / 2, ch / 2);
  // Flip applied in screen axes (outermost), rotation innermost.
  ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -w / 2, -h / 2, w, h);

  return { canvas, url: canvas.toDataURL("image/png"), w: cw, h: ch };
}

/**
 * The RGBA pixels of a picture, read at most ALPHA_CHECK_MAX_SIDE across; null when the browser will not let us read them
 * (a cross-origin picture). The canvas is released at once: iOS Safari counts canvas memory until a collection and
 * refuses past its limit.
 */
function readImagePixels(img: HTMLImageElement): ArrayLike<number> | null {
  const fit = Math.min(1, ALPHA_CHECK_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight, 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(img.naturalWidth * fit));
  canvas.height = Math.max(1, Math.round(img.naturalHeight * fit));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });

  try {
    if (!ctx) {
      return null;
    }

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return readContextPixels(ctx, canvas.width, canvas.height);
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** Crop a rectangle (in source-canvas pixels) to a blob, respecting maxDim. */
function drawCrop(
  src: HTMLCanvasElement,
  cx: number,
  cy: number,
  cw: number,
  ch: number,
  maxDim: number,
  quality: number
): Promise<Blob | null> {
  return new Promise((resolve) => {
    const sx = clamp(cx, 0, src.width);
    const sy = clamp(cy, 0, src.height);
    const sw = clamp(cw, 1, src.width - sx);
    const sh = clamp(ch, 1, src.height - sy);

    let outW = Math.round(sw);
    let outH = Math.round(sh);
    if (outW > maxDim || outH > maxDim) {
      if (outW >= outH) {
        outH = Math.round((outH / outW) * maxDim);
        outW = maxDim;
      } else {
        outW = Math.round((outW / outH) * maxDim);
        outH = maxDim;
      }
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, outW);
    canvas.height = Math.max(1, outH);
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      resolve(null);
      return;
    }
    ctx.drawImage(src, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
    // ⚠ The format follows the pixels WRITTEN (#135): never the type the file claimed (a browser types a picked file by
    //   its extension), and a crop that cuts the transparency away is a JPEG again. A JPEG is laid on white first.
    const mime = encodeMimeFor(ctx, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        canvas.width = 0;
        canvas.height = 0;
        resolve(blob);
      },
      mime,
      quality
    );
  });
}

// ============================================
// CornerBrackets (fixed-mode frame decoration)
// ============================================

const CornerBrackets: React.FC = React.memo(() => {
  const s = CORNER_STROKE;
  const len = CORNER_LEN;
  const size = len + s;
  const base: React.CSSProperties = { position: "absolute", width: size, height: size, overflow: "visible" };

  return (
    <>
      <svg style={{ ...base, top: -s / 2, left: -s / 2 }} fill="none">
        <path d={`M ${s / 2} ${len} L ${s / 2} ${s / 2} L ${len} ${s / 2}`} stroke="white" strokeWidth={s} strokeLinecap="round" />
      </svg>
      <svg style={{ ...base, top: -s / 2, right: -s / 2 }} fill="none">
        <path d={`M ${size - len} ${s / 2} L ${size - s / 2} ${s / 2} L ${size - s / 2} ${len}`} stroke="white" strokeWidth={s} strokeLinecap="round" />
      </svg>
      <svg style={{ ...base, bottom: -s / 2, left: -s / 2 }} fill="none">
        <path d={`M ${s / 2} ${size - len} L ${s / 2} ${size - s / 2} L ${len} ${size - s / 2}`} stroke="white" strokeWidth={s} strokeLinecap="round" />
      </svg>
      <svg style={{ ...base, bottom: -s / 2, right: -s / 2 }} fill="none">
        <path d={`M ${size - s / 2} ${size - len} L ${size - s / 2} ${size - s / 2} L ${size - len} ${size - s / 2}`} stroke="white" strokeWidth={s} strokeLinecap="round" />
      </svg>
    </>
  );
});
CornerBrackets.displayName = "CornerBrackets";

// ============================================
// GridLines (rule of thirds)
// ============================================

const GridLines: React.FC<{ visible: boolean }> = React.memo(({ visible }) => (
  <Box style={{ position: "absolute", inset: 0, opacity: visible ? 1 : 0, transition: "opacity 250ms ease", pointerEvents: "none" }}>
    {[1, 2].map((i) => (
      <React.Fragment key={`g${i}`}>
        <Box style={{ position: "absolute", left: `${(i / 3) * 100}%`, top: 0, bottom: 0, width: 1, backgroundColor: "rgba(255,255,255,0.25)" }} />
        <Box style={{ position: "absolute", top: `${(i / 3) * 100}%`, left: 0, right: 0, height: 1, backgroundColor: "rgba(255,255,255,0.25)" }} />
      </React.Fragment>
    ))}
  </Box>
));
GridLines.displayName = "GridLines";

// Free-mode resize handles: 8 positions as fractions of the frame.
const FREE_HANDLES: { key: string; fx: number; fy: number; cursor: string }[] = [
  { key: "nw", fx: 0, fy: 0, cursor: "nwse-resize" },
  { key: "n", fx: 0.5, fy: 0, cursor: "ns-resize" },
  { key: "ne", fx: 1, fy: 0, cursor: "nesw-resize" },
  { key: "e", fx: 1, fy: 0.5, cursor: "ew-resize" },
  { key: "se", fx: 1, fy: 1, cursor: "nwse-resize" },
  { key: "s", fx: 0.5, fy: 1, cursor: "ns-resize" },
  { key: "sw", fx: 0, fy: 1, cursor: "nesw-resize" },
  { key: "w", fx: 0, fy: 0.5, cursor: "ew-resize" },
];

// ============================================
// Main Component
// ============================================

interface FreeFrame {
  x: number;
  y: number;
  w: number;
  h: number;
}

const ImageCropEditorComponent: React.FC<ImageCropEditorProps> = ({
  opened,
  onClose,
  onConfirm,
  onSkip,
  image,
  aspectRatio = "free",
  showAspectRatioSelector = true,
  showSkip = true,
  showRotation = true,
  showBackgroundRemoval = true,
  maxOutputDimension = DEFAULT_MAX_OUTPUT,
  outputQuality = DEFAULT_QUALITY,
  title,
  aspectChoices,
}) => {
  const { t } = useTranslation(["common"]);

  // A2e #91 — this full-screen editor is an overlay of its own: while it is open the drawer it was opened from must not
  // take Esc (it would close the drawer and throw away the form behind the editor). On the stack it is the TOP overlay
  // (AppDrawer / ConfirmModal stop closing on Esc), and its root carries OWN_ESCAPE_ATTR, so an Esc pressed inside it is
  // marked for every Mantine drawer (overlayStack). Esc closes the editor itself.
  useOverlayStack(opened);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const presets = useMemo(
    () => (aspectChoices && aspectChoices.length > 0 ? ASPECT_PRESETS.filter((p) => aspectChoices.includes(p.value)) : ASPECT_PRESETS),
    [aspectChoices]
  );

  // ── Source image (original, or background-removed) ──
  const [sourceEl, setSourceEl] = useState<HTMLImageElement | null>(null);
  const [sourceHasAlpha, setSourceHasAlpha] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalMeta, setOriginalMeta] = useState<{ w: number; h: number; size: number } | null>(null);

  // Guards so a background-color swap (same dimensions) doesn't reset pan/zoom.
  const lastFixedResetKey = useRef<string>("");
  const lastFreeResetKey = useRef<string>("");

  const sourceObjUrlRef = useRef<string | null>(null); // object URL to revoke
  const removedBlobRef = useRef<Blob | null>(null); // transparent cut-out, for recoloring
  const originalBlobRef = useRef<Blob | null>(null); // untouched original, for Skip

  // ── Transform state ──
  const [selectedAspect, setSelectedAspect] = useState<string>(typeof aspectRatio === "number" ? "free" : aspectRatio);
  const [rotation, setRotation] = useState(0);
  const [flipH, setFlipH] = useState(false);
  const [flipV, setFlipV] = useState(false);

  // ── Working (baked) image ──
  const [working, setWorking] = useState<{ canvas: HTMLCanvasElement; url: string; w: number; h: number } | null>(null);

  // ── Fixed-mode pan/zoom ──
  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);

  // ── Free-mode crop rectangle (viewport coords) ──
  const [freeFrame, setFreeFrame] = useState<FreeFrame | null>(null);

  // ── Background removal ──
  const [bgBusy, setBgBusy] = useState(false);
  const [bgProgress, setBgProgress] = useState<{ percent: number; label: string } | null>(null);
  const [bgApplied, setBgApplied] = useState(false);
  const [bgColorKey, setBgColorKey] = useState<string>("transparent");
  const [bgError, setBgError] = useState<string | null>(null);

  // ── Grid visibility (fixed-mode gestures) ──
  const [isGesturing, setIsGesturing] = useState(false);
  const gridTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showGrid = useCallback(() => {
    if (gridTimerRef.current) {
      clearTimeout(gridTimerRef.current);
    }
    setIsGesturing(true);
  }, []);
  const hideGridSoon = useCallback(() => {
    if (gridTimerRef.current) {
      clearTimeout(gridTimerRef.current);
    }
    gridTimerRef.current = setTimeout(() => setIsGesturing(false), GRID_FADE_DELAY_MS);
  }, []);
  useEffect(
    () => () => {
      if (gridTimerRef.current) {
        clearTimeout(gridTimerRef.current);
      }
    },
    []
  );

  // ── Viewport measurement ──
  const vpRef = useRef<HTMLDivElement>(null);
  const [vpSize, setVpSize] = useState({ w: 0, h: 0 });

  // ── Fixed-mode gesture refs ──
  const gRef = useRef({ active: false, pinching: false, sx: 0, sy: 0, sox: 0, soy: 0, pDist: 0, pScale: 1 });

  // ── Free-mode drag ref ──
  const freeDragRef = useRef<{ type: string; sx: number; sy: number; frame: FreeFrame } | null>(null);

  // ── Computed ──
  const effectiveRatio = useMemo(() => {
    if (typeof aspectRatio === "number") {
      return aspectRatio;
    }
    return parseAspectRatio(selectedAspect as CropAspect);
  }, [aspectRatio, selectedAspect]);

  const isFree = effectiveRatio == null;

  const cropFrame = useMemo(() => computeCropFrame(vpSize.w, vpSize.h, effectiveRatio), [vpSize.w, vpSize.h, effectiveRatio]);

  const containFit = useMemo(() => {
    if (!working || vpSize.w === 0) {
      return null;
    }
    return computeContainFit(vpSize.w, vpSize.h, working.w, working.h);
  }, [working, vpSize.w, vpSize.h]);

  const minScale = useMemo(() => {
    if (!working) {
      return 1;
    }
    return computeMinScale(working.w, working.h, cropFrame.width, cropFrame.height);
  }, [working, cropFrame.width, cropFrame.height]);

  const maxScale = minScale * MAX_ZOOM_MULTIPLIER;
  const isReady = !!working && vpSize.w > 0;

  // ── Load a blob as the current source ──
  const loadSourceBlob = useCallback((blob: Blob, hasAlpha: boolean, isOriginal = false) => {
    if (sourceObjUrlRef.current) {
      URL.revokeObjectURL(sourceObjUrlRef.current);
    }
    const url = URL.createObjectURL(blob);
    sourceObjUrlRef.current = url;
    setIsLoadingImage(true);
    const img = new Image();
    img.onload = () => {
      setSourceEl(img);
      // The picture as given is LOOKED AT, whatever type it claims (#135), so the editor shows its transparency on a
      // checkerboard instead of black. A cut-out says so itself; a colour fill is opaque. (The saved format is decided
      // again from the crop's own pixels — drawCrop.)
      setSourceHasAlpha(hasAlpha || (isOriginal && imageHasAlpha(() => readImagePixels(img))));
      setIsLoadingImage(false);
      if (isOriginal) {
        setOriginalMeta({ w: img.naturalWidth, h: img.naturalHeight, size: blob.size });
      }
    };
    img.onerror = () => setIsLoadingImage(false);
    img.src = url;
  }, []);

  // ── Load original when opened / image changes ──
  useEffect(() => {
    if (!opened || !image) {
      setSourceEl(null);
      setWorking(null);
      return;
    }

    // Reset all editing state.
    setRotation(0);
    setFlipH(false);
    setFlipV(false);
    setSelectedAspect(typeof aspectRatio === "number" ? "free" : aspectRatio);
    setIsProcessing(false);
    setIsGesturing(false);
    setBgApplied(false);
    setBgBusy(false);
    setBgProgress(null);
    setBgColorKey("transparent");
    setBgError(null);
    setOriginalMeta(null);
    setSourceHasAlpha(false);
    removedBlobRef.current = null;
    lastFixedResetKey.current = "";
    lastFreeResetKey.current = "";

    if (typeof image === "string") {
      // A read with a limit (ROADMAP #131): an image that never arrives ends the spinner instead of holding it for good.
      fetchWithLimit(image, {}, { limitMs: 60_000, isWrite: false })
        .then((r) => r.blob())
        .then((blob) => {
          originalBlobRef.current = blob;
          loadSourceBlob(blob, false, true);
        })
        .catch(() => setIsLoadingImage(false));
    } else {
      originalBlobRef.current = image;
      loadSourceBlob(image, false, true);
    }

    return () => {
      if (sourceObjUrlRef.current) {
        URL.revokeObjectURL(sourceObjUrlRef.current);
        sourceObjUrlRef.current = null;
      }
    };
  }, [opened, image, aspectRatio, loadSourceBlob]);

  // ── Bake rotation + flip into the working canvas ──
  useEffect(() => {
    if (!sourceEl) {
      setWorking(null);
      return;
    }
    setWorking(bakeImage(sourceEl, rotation, flipH, flipV));
  }, [sourceEl, rotation, flipH, flipV]);

  // ── Reset fixed-mode pan/zoom only when dimensions actually change ──
  // (a background-color swap produces a new working image with the SAME size —
  // we must NOT snap the user's zoom/pan back to default in that case).
  useEffect(() => {
    if (!working || isFree) {
      return;
    }
    const key = `${working.w}x${working.h}|${cropFrame.width}x${cropFrame.height}`;
    if (lastFixedResetKey.current === key) {
      return;
    }
    lastFixedResetKey.current = key;
    setScale(computeMinScale(working.w, working.h, cropFrame.width, cropFrame.height));
    setOffsetX(0);
    setOffsetY(0);
  }, [working, cropFrame.width, cropFrame.height, isFree]);

  // ── Reset free-mode frame to the full image only when dimensions change ──
  useEffect(() => {
    if (!isFree || !containFit || !working) {
      return;
    }
    const key = `${working.w}x${working.h}|${Math.round(vpSize.w)}x${Math.round(vpSize.h)}`;
    if (lastFreeResetKey.current === key) {
      return;
    }
    lastFreeResetKey.current = key;
    setFreeFrame({ x: containFit.left, y: containFit.top, w: containFit.width, h: containFit.height });
  }, [isFree, containFit, working, vpSize.w, vpSize.h]);

  // ── Measure viewport ──
  // ⚠⚠ WATCH THE VIEWPORT ITSELF, not the window. The ratio strip and the toolbar below it render only once the editor
  // is READY — which needs this very measurement — so the first one is taken with the controls absent and the viewport
  // taller than it ends up. The controls then take their room, the window never resizes, and a free-mode image fitted
  // to the stale height ran ~170px UNDER the controls: a letter-size poster read as cut (owner, 2026-10-02, #79). A fixed
  // ratio's frame was sized from the same stale number. A ResizeObserver sees the viewport shrink and re-fits.
  useEffect(() => {
    if (!opened) {
      return;
    }
    const measure = () => {
      if (vpRef.current) {
        const r = vpRef.current.getBoundingClientRect();
        setVpSize((prev) => (prev.w === r.width && prev.h === r.height ? prev : { w: r.width, h: r.height }));
      }
    };
    // ⚠ Attached on the first frame, not here: the viewport sits inside the modal's Transition and may not be mounted
    //   yet when this effect runs — an observer created now would have nothing to watch, silently.
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    const raf = requestAnimationFrame(() => {
      measure();
      if (observer && vpRef.current) {
        observer.observe(vpRef.current);
      }
    });
    window.addEventListener("resize", measure);
    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [opened]);

  // ── Fixed-mode clamp ──
  const doClamp = useCallback(
    (nx: number, ny: number, s: number) => {
      if (!working) {
        return;
      }
      const c = clampOffset(nx, ny, working.w, working.h, cropFrame.width, cropFrame.height, s);
      setOffsetX(c.x);
      setOffsetY(c.y);
    },
    [working, cropFrame.width, cropFrame.height]
  );

  // ============================================
  // Fixed-mode touch handlers
  // ============================================

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      showGrid();
      const g = gRef.current;
      if (e.touches.length === 1) {
        g.active = true;
        g.pinching = false;
        g.sx = e.touches[0].clientX;
        g.sy = e.touches[0].clientY;
        g.sox = offsetX;
        g.soy = offsetY;
      } else if (e.touches.length === 2) {
        g.active = false;
        g.pinching = true;
        g.pDist = touchDist(e.touches[0], e.touches[1]);
        g.pScale = scale;
        g.sox = offsetX;
        g.soy = offsetY;
        g.sx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        g.sy = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      }
    },
    [offsetX, offsetY, scale, showGrid]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      showGrid();
      const g = gRef.current;
      if (g.pinching && e.touches.length === 2) {
        const dist = touchDist(e.touches[0], e.touches[1]);
        const ns = clamp(g.pScale * (dist / g.pDist), minScale, maxScale);
        setScale(ns);
        const mx = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        doClamp(g.sox + mx - g.sx, g.soy + my - g.sy, ns);
      } else if (g.active && e.touches.length === 1) {
        doClamp(g.sox + e.touches[0].clientX - g.sx, g.soy + e.touches[0].clientY - g.sy, scale);
      }
    },
    [minScale, maxScale, scale, doClamp, showGrid]
  );

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      hideGridSoon();
      const g = gRef.current;
      if (e.touches.length === 0) {
        g.active = false;
        g.pinching = false;
      } else if (e.touches.length === 1) {
        g.pinching = false;
        g.active = true;
        g.sx = e.touches[0].clientX;
        g.sy = e.touches[0].clientY;
        g.sox = offsetX;
        g.soy = offsetY;
      }
    },
    [offsetX, offsetY, hideGridSoon]
  );

  // ============================================
  // Fixed-mode mouse handlers
  // ============================================

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) {
        return;
      }
      e.preventDefault();
      showGrid();
      const g = gRef.current;
      g.active = true;
      g.sx = e.clientX;
      g.sy = e.clientY;
      g.sox = offsetX;
      g.soy = offsetY;
    },
    [offsetX, offsetY, showGrid]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const g = gRef.current;
      if (!g.active) {
        return;
      }
      showGrid();
      doClamp(g.sox + e.clientX - g.sx, g.soy + e.clientY - g.sy, scale);
    },
    [scale, doClamp, showGrid]
  );

  const handleMouseUp = useCallback(() => {
    gRef.current.active = false;
    hideGridSoon();
  }, [hideGridSoon]);

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      showGrid();
      hideGridSoon();
      const factor = e.deltaY > 0 ? 0.95 : 1.05;
      const ns = clamp(scale * factor, minScale, maxScale);
      setScale(ns);
      doClamp(offsetX, offsetY, ns);
    },
    [minScale, maxScale, scale, offsetX, offsetY, doClamp, showGrid, hideGridSoon]
  );

  // ============================================
  // Free-mode frame drag (pointer events)
  // ============================================

  const beginFreeDrag = useCallback(
    (type: string) => (e: React.PointerEvent) => {
      if (!freeFrame) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      freeDragRef.current = { type, sx: e.clientX, sy: e.clientY, frame: { ...freeFrame } };
      vpRef.current?.setPointerCapture(e.pointerId);
    },
    [freeFrame]
  );

  const handleFreePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = freeDragRef.current;
      if (!d || !containFit) {
        return;
      }
      const dx = e.clientX - d.sx;
      const dy = e.clientY - d.sy;
      const dispLeft = containFit.left;
      const dispTop = containFit.top;
      const dispRight = containFit.left + containFit.width;
      const dispBottom = containFit.top + containFit.height;

      if (d.type === "move") {
        const nx = clamp(d.frame.x + dx, dispLeft, dispRight - d.frame.w);
        const ny = clamp(d.frame.y + dy, dispTop, dispBottom - d.frame.h);
        setFreeFrame({ x: nx, y: ny, w: d.frame.w, h: d.frame.h });
        return;
      }

      let left = d.frame.x;
      let top = d.frame.y;
      let right = d.frame.x + d.frame.w;
      let bottom = d.frame.y + d.frame.h;

      if (d.type.includes("w")) {
        left = clamp(d.frame.x + dx, dispLeft, right - MIN_FRAME_PX);
      }
      if (d.type.includes("e")) {
        right = clamp(d.frame.x + d.frame.w + dx, left + MIN_FRAME_PX, dispRight);
      }
      if (d.type.includes("n")) {
        top = clamp(d.frame.y + dy, dispTop, bottom - MIN_FRAME_PX);
      }
      if (d.type.includes("s")) {
        bottom = clamp(d.frame.y + d.frame.h + dy, top + MIN_FRAME_PX, dispBottom);
      }

      setFreeFrame({ x: left, y: top, w: right - left, h: bottom - top });
    },
    [containFit]
  );

  const handleFreePointerUp = useCallback((e: React.PointerEvent) => {
    if (freeDragRef.current) {
      freeDragRef.current = null;
      vpRef.current?.releasePointerCapture(e.pointerId);
    }
  }, []);

  // ============================================
  // Actions
  // ============================================

  const handleRotateCW = useCallback(() => setRotation((r) => (r + 90) % 360), []);
  const handleRotateCCW = useCallback(() => setRotation((r) => (r + 270) % 360), []);
  const handleFlipH = useCallback(() => setFlipH((v) => !v), []);
  const handleFlipV = useCallback(() => setFlipV((v) => !v), []);
  const handleAspectChange = useCallback((v: string) => setSelectedAspect(v), []);

  const handleResetZoom = useCallback(() => {
    setScale(minScale);
    setOffsetX(0);
    setOffsetY(0);
    if (isFree && containFit) {
      setFreeFrame({ x: containFit.left, y: containFit.top, w: containFit.width, h: containFit.height });
    }
  }, [minScale, isFree, containFit]);

  // ── Background removal ──
  const handleRemoveBackground = useCallback(async () => {
    const src = originalBlobRef.current;
    if (!src) {
      return;
    }
    setBgBusy(true);
    setBgError(null);
    setBgProgress({ percent: 0, label: "Loading model" });
    try {
      const cut = await removeBackground(src, {
        onProgress: (percent, label) => setBgProgress({ percent, label }),
      });
      removedBlobRef.current = cut;
      setBgApplied(true);
      setBgColorKey("transparent");
      // Keep the current rotation/flip/zoom — the cut-out is the same size, so
      // the view stays exactly where the user left it.
      loadSourceBlob(cut, true);
    } catch (err) {
      setBgError(
        t("common:imageCrop.bgError", {
          defaultValue: "Couldn't remove the background. Try a clearer photo of the subject.",
        })
      );
      console.error("Background removal failed:", err);
    } finally {
      setBgBusy(false);
      setBgProgress(null);
    }
  }, [loadSourceBlob, t]);

  const handleBgColorChange = useCallback(
    async (key: string, color: string | null) => {
      const cut = removedBlobRef.current;
      if (!cut) {
        return;
      }
      setBgColorKey(key);
      setBgBusy(true);
      try {
        if (color == null) {
          loadSourceBlob(cut, true);
        } else {
          const filled = await compositeOnColor(cut, color);
          loadSourceBlob(filled, false);
        }
      } catch {
        // keep previous source
      } finally {
        setBgBusy(false);
      }
    },
    [loadSourceBlob]
  );

  const handleConfirm = useCallback(async () => {
    if (!working) {
      return;
    }
    setIsProcessing(true);

    let blob: Blob | null = null;

    if (isFree && freeFrame && containFit) {
      const fit = containFit.scale;
      const cx = (freeFrame.x - containFit.left) / fit;
      const cy = (freeFrame.y - containFit.top) / fit;
      const cw = freeFrame.w / fit;
      const ch = freeFrame.h / fit;
      blob = await drawCrop(working.canvas, cx, cy, cw, ch, maxOutputDimension, outputQuality);
    } else {
      const cx = (working.w * scale) / 2 - offsetX - cropFrame.width / 2;
      const cy = (working.h * scale) / 2 - offsetY - cropFrame.height / 2;
      blob = await drawCrop(
        working.canvas,
        cx / scale,
        cy / scale,
        cropFrame.width / scale,
        cropFrame.height / scale,
        maxOutputDimension,
        outputQuality
      );
    }

    setIsProcessing(false);
    if (blob) {
      onConfirm(blob);
    }
  }, [
    working,
    isFree,
    freeFrame,
    containFit,
    scale,
    offsetX,
    offsetY,
    cropFrame.width,
    cropFrame.height,
    maxOutputDimension,
    outputQuality,
    onConfirm,
  ]);

  const handleSkip = useCallback(() => {
    const orig = originalBlobRef.current;
    if (orig && onSkip) {
      onSkip(orig);
    }
  }, [onSkip]);

  // ============================================
  // Styles
  // ============================================

  const fixedImgStyle = useMemo((): React.CSSProperties => {
    if (!working) {
      return {};
    }
    return {
      position: "absolute",
      left: "50%",
      top: "50%",
      transformOrigin: "center center",
      transform: `translate(-50%, -50%) translate(${offsetX}px, ${offsetY}px) scale(${scale})`,
      width: working.w,
      height: working.h,
      maxWidth: "none",
      maxHeight: "none",
      userSelect: "none",
      pointerEvents: "none",
      willChange: "transform",
    };
  }, [working, offsetX, offsetY, scale]);

  const overlayStyle = useMemo((): React.CSSProperties => {
    if (vpSize.w === 0) {
      return {};
    }
    let fl: number;
    let ft: number;
    let fr: number;
    let fb: number;
    if (isFree && freeFrame) {
      fl = freeFrame.x;
      ft = freeFrame.y;
      fr = freeFrame.x + freeFrame.w;
      fb = freeFrame.y + freeFrame.h;
    } else {
      fl = (vpSize.w - cropFrame.width) / 2;
      ft = (vpSize.h - cropFrame.height) / 2;
      fr = fl + cropFrame.width;
      fb = ft + cropFrame.height;
    }
    return {
      position: "absolute",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.55)",
      clipPath: `polygon(0% 0%, 0% 100%, ${fl}px 100%, ${fl}px ${ft}px, ${fr}px ${ft}px, ${fr}px ${fb}px, ${fl}px ${fb}px, ${fl}px 100%, 100% 100%, 100% 0%)`,
      pointerEvents: "none",
      zIndex: 2,
    };
  }, [vpSize, cropFrame, isFree, freeFrame]);

  const fixedFrameStyle = useMemo(
    (): React.CSSProperties => ({
      position: "absolute",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      width: cropFrame.width,
      height: cropFrame.height,
      pointerEvents: "none",
      zIndex: 3,
    }),
    [cropFrame]
  );

  // ── Info readout: crop size / coords / aspect + output size ──
  const editInfo = useMemo(() => {
    if (!working) {
      return null;
    }
    const cap = maxOutputDimension;
    const capped = (w: number, h: number) => {
      let ow = Math.round(w);
      let oh = Math.round(h);
      if (ow > cap || oh > cap) {
        if (ow >= oh) {
          oh = Math.round((oh / ow) * cap);
          ow = cap;
        } else {
          ow = Math.round((ow / oh) * cap);
          oh = cap;
        }
      }
      return { w: ow, h: oh };
    };

    if (isFree && freeFrame && containFit) {
      const fit = containFit.scale;
      const cw = freeFrame.w / fit;
      const ch = freeFrame.h / fit;
      const tlx = Math.round((freeFrame.x - containFit.left) / fit);
      const tly = Math.round((freeFrame.y - containFit.top) / fit);
      return {
        label: t("common:imageCrop.selection", { defaultValue: "Selection" }),
        cropW: Math.round(cw),
        cropH: Math.round(ch),
        ratio: ratioLabel(cw, ch),
        tl: { x: tlx, y: tly },
        br: { x: tlx + Math.round(cw), y: tly + Math.round(ch) },
        out: capped(cw, ch),
      };
    }

    const cw = cropFrame.width / scale;
    const ch = cropFrame.height / scale;
    return {
      label: t("common:imageCrop.crop", { defaultValue: "Crop" }),
      cropW: Math.round(cw),
      cropH: Math.round(ch),
      ratio: ratioLabel(cw, ch),
      tl: null as { x: number; y: number } | null,
      br: null as { x: number; y: number } | null,
      out: capped(cw, ch),
    };
  }, [working, isFree, freeFrame, containFit, cropFrame.width, cropFrame.height, scale, maxOutputDimension, t]);

  // ============================================
  // Render
  // ============================================

  if (!opened) {
    return null;
  }

  const displayTitle = title || t("common:imageCrop.editTitle", { defaultValue: "Edit Image" });
  const showAspectPills = showAspectRatioSelector && typeof aspectRatio !== "number";
  const controlBtnStyles = { root: { color: "white", minWidth: 44, minHeight: 44 } };

  return (
    <Portal>
      <Transition mounted={opened} transition="fade" duration={200}>
        {(transStyles) => (
          <Box
            ref={(el: HTMLDivElement | null) => {
              // Focus moves INTO the editor when it opens, so an Esc lands inside it (see OWN_ESCAPE_ATTR above).
              if (el && rootRef.current !== el) {
                rootRef.current = el;
                el.focus({ preventScroll: true });
              }
            }}
            tabIndex={-1}
            {...{ [OWN_ESCAPE_ATTR]: "true" }}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key === "Escape" && !isProcessing) {
                event.stopPropagation();
                onClose();
              }
            }}
            style={{
              ...transStyles,
              outline: "none",
              position: "fixed",
              inset: 0,
              zIndex: 1000,
              backgroundColor: "#000",
              display: "flex",
              flexDirection: "column",
              touchAction: "none",
              overscrollBehavior: "none",
            }}
          >
            {/* ═══════ Top bar ═══════ */}
            <Box
              style={{
                position: "relative",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 12px",
                paddingTop: "max(8px, env(safe-area-inset-top))",
                minHeight: 48,
              }}
            >
              <ActionIcon variant="subtle" color="gray" size="lg" onClick={onClose} aria-label={t("common:actions.close", { defaultValue: "Close" })} styles={controlBtnStyles}>
                <AppIcon iconName="X" size={22} />
              </ActionIcon>

              <Text size="md" fw={600} c="white" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.5)" }}>
                {displayTitle}
              </Text>

              <Tooltip label={t("common:imageCrop.resetZoom", { defaultValue: "Reset" })} position="bottom" withArrow>
                <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleResetZoom} disabled={!isReady} aria-label={t("common:imageCrop.resetZoom", { defaultValue: "Reset" })} styles={controlBtnStyles}>
                  <AppIcon iconName="FocusCentered" size={22} />
                </ActionIcon>
              </Tooltip>
            </Box>

            {/* ═══════ Viewport ═══════ */}
            <Box
              ref={vpRef}
              style={{
                flex: 1,
                position: "relative",
                overflow: "hidden",
                cursor: isReady && !isFree ? "grab" : "default",
                touchAction: "none",
                // Fixed mode: the cover image fills the viewport, so checker the
                // whole area. Free mode uses a checker sized to the image (below)
                // so the letterbox stays clean instead of tiling into the margins.
                ...(sourceHasAlpha && !isFree ? CHECKER_BG : {}),
              }}
              onTouchStart={isReady && !isFree ? handleTouchStart : undefined}
              onTouchMove={isReady && !isFree ? handleTouchMove : undefined}
              onTouchEnd={isReady && !isFree ? handleTouchEnd : undefined}
              onMouseDown={isReady && !isFree ? handleMouseDown : undefined}
              onMouseMove={isReady && !isFree ? handleMouseMove : undefined}
              onMouseUp={isReady && !isFree ? handleMouseUp : undefined}
              onMouseLeave={isReady && !isFree ? handleMouseUp : undefined}
              onWheel={isReady && !isFree ? handleWheel : undefined}
              onPointerMove={isReady && isFree ? handleFreePointerMove : undefined}
              onPointerUp={isReady && isFree ? handleFreePointerUp : undefined}
            >
              {/* Loading spinner */}
              {(isLoadingImage || bgBusy) && (
                <Box style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 12, alignItems: "center", justifyContent: "center", zIndex: 6, backgroundColor: "rgba(0,0,0,0.4)" }}>
                  <Loader color="white" size="md" />
                  {bgProgress && (
                    <Text c="white" size="md">
                      {bgProgress.label}
                      {bgProgress.percent ? ` · ${bgProgress.percent}%` : ""}
                    </Text>
                  )}
                </Box>
              )}

              {/* ── FIXED mode: image behind a centered frame ── */}
              {!isFree && working && <img src={working.url} alt="" style={fixedImgStyle} draggable={false} />}

              {/* ── FREE mode: contain-fit image ── */}
              {isFree && working && containFit && sourceHasAlpha && (
                <Box
                  style={{
                    position: "absolute",
                    left: containFit.left,
                    top: containFit.top,
                    width: containFit.width,
                    height: containFit.height,
                    zIndex: 0,
                    ...CHECKER_BG,
                  }}
                />
              )}
              {isFree && working && containFit && (
                <img
                  src={working.url}
                  alt=""
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: containFit.left,
                    top: containFit.top,
                    width: containFit.width,
                    height: containFit.height,
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              )}

              {/* Dark overlay with transparent crop hole */}
              {isReady && <Box style={overlayStyle} />}

              {/* Fixed-mode frame */}
              {isReady && !isFree && (
                <Box style={fixedFrameStyle}>
                  <Box style={{ position: "absolute", inset: 0, border: "1px solid rgba(255,255,255,0.35)" }} />
                  <GridLines visible={isGesturing} />
                  <CornerBrackets />
                </Box>
              )}

              {/* Free-mode resizable frame */}
              {isReady && isFree && freeFrame && (
                <Box
                  style={{
                    position: "absolute",
                    left: freeFrame.x,
                    top: freeFrame.y,
                    width: freeFrame.w,
                    height: freeFrame.h,
                    zIndex: 3,
                    cursor: "move",
                    touchAction: "none",
                  }}
                  onPointerDown={beginFreeDrag("move")}
                >
                  <Box style={{ position: "absolute", inset: 0, border: "1px solid rgba(255,255,255,0.9)", boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }} />
                  <GridLines visible />
                  {FREE_HANDLES.map((h) => (
                    <Box
                      key={h.key}
                      onPointerDown={beginFreeDrag(h.key)}
                      style={{
                        position: "absolute",
                        left: `${h.fx * 100}%`,
                        top: `${h.fy * 100}%`,
                        width: 22,
                        height: 22,
                        transform: "translate(-50%, -50%)",
                        cursor: h.cursor,
                        touchAction: "none",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Box style={{ width: 12, height: 12, borderRadius: 3, backgroundColor: "white", boxShadow: "0 0 0 1px rgba(0,0,0,0.4)" }} />
                    </Box>
                  ))}
                </Box>
              )}

              {/* Info readout (dimensions / coords / aspect) */}
              {isReady && editInfo && (
                <Box
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    zIndex: 8,
                    pointerEvents: "none",
                    padding: "6px 9px",
                    borderRadius: 6,
                    backgroundColor: "rgba(0,0,0,0.55)",
                    backdropFilter: "blur(3px)",
                    fontFamily: "var(--mantine-font-family-monospace, monospace)",
                    fontSize: 11,
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,0.9)",
                    maxWidth: "min(70vw, 320px)",
                  }}
                >
                  {originalMeta && (
                    <div style={{ color: "rgba(255,255,255,0.65)" }}>
                      {t("common:imageCrop.original", { defaultValue: "Original" })} {originalMeta.w}×{originalMeta.h} · {ratioLabel(originalMeta.w, originalMeta.h)} · {formatFileSize(originalMeta.size)}
                    </div>
                  )}
                  <div>
                    {editInfo.label} {editInfo.cropW}×{editInfo.cropH} · {editInfo.ratio}
                  </div>
                  {editInfo.tl && editInfo.br && (
                    <div style={{ color: "rgba(255,255,255,0.65)" }}>
                      ↖ {editInfo.tl.x},{editInfo.tl.y}   ↘ {editInfo.br.x},{editInfo.br.y}
                    </div>
                  )}
                  <div style={{ color: "var(--mantine-color-teal-3)" }}>
                    {t("common:imageCrop.output", { defaultValue: "Output" })} {editInfo.out.w}×{editInfo.out.h}
                  </div>
                </Box>
              )}
            </Box>

            {/* ═══════ Background color row (after removal) ═══════ */}
            {isReady && bgApplied && (
              <Box style={{ position: "relative", zIndex: 10, padding: "8px 16px 0" }}>
                <Group justify="center" gap="xs" wrap="nowrap" style={{ overflowX: "auto" }}>
                  {BG_FILL_COLORS.map((c) => {
                    const active = bgColorKey === c.key;
                    return (
                      <Tooltip key={c.key} label={c.label} position="top" withArrow>
                        <Box
                          onClick={() => handleBgColorChange(c.key, c.value)}
                          style={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            cursor: "pointer",
                            flexShrink: 0,
                            border: active ? "2px solid var(--mantine-color-blue-4)" : "2px solid rgba(255,255,255,0.25)",
                            boxShadow: active ? "0 0 0 2px rgba(34,139,230,0.4)" : undefined,
                            ...(c.swatch === "checker" ? CHECKER_BG : { backgroundColor: c.swatch }),
                          }}
                        />
                      </Tooltip>
                    );
                  })}
                </Group>
              </Box>
            )}

            {/* ═══════ Aspect + transform controls ═══════ */}
            {isReady && (showAspectPills || showRotation || showBackgroundRemoval) && (
              <Box style={{ position: "relative", zIndex: 10, padding: "10px 12px 4px" }}>
                {/* Aspect pills (scrollable) */}
                {showAspectPills && (
                  <Box style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", paddingBottom: 8 }}>
                    <Group gap={6} wrap="nowrap" justify="center" style={{ minWidth: "min-content", padding: "0 4px" }}>
                      {presets.map((p) => {
                        const active = selectedAspect === p.value;
                        return (
                          <Box
                            key={p.value}
                            role="button"
                            onClick={() => handleAspectChange(p.value)}
                            style={{
                              padding: "6px 12px",
                              borderRadius: 8,
                              whiteSpace: "nowrap",
                              fontSize: 12,
                              fontWeight: 600,
                              minHeight: 32,
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              userSelect: "none",
                              backgroundColor: active ? "white" : "rgba(255,255,255,0.12)",
                              color: active ? "#111" : "rgba(255,255,255,0.78)",
                            }}
                          >
                            {p.label}
                          </Box>
                        );
                      })}
                    </Group>
                  </Box>
                )}

                {/* Rotate / flip / bg-removal */}
                <Group justify="center" gap="xs" wrap="nowrap">
                  {showRotation && (
                    <>
                      <Tooltip label={t("common:imageCrop.rotateCCW", { defaultValue: "Rotate left" })} position="top" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleRotateCCW} aria-label={t("common:imageCrop.rotateCCW", { defaultValue: "Rotate left" })} styles={controlBtnStyles}>
                          <AppIcon iconName="Rotate" size={20} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common:imageCrop.rotateCW", { defaultValue: "Rotate right" })} position="top" withArrow>
                        <ActionIcon variant="subtle" color="gray" size="lg" onClick={handleRotateCW} aria-label={t("common:imageCrop.rotateCW", { defaultValue: "Rotate right" })} styles={controlBtnStyles}>
                          <AppIcon iconName="RotateClockwise" size={20} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common:imageCrop.flipH", { defaultValue: "Flip horizontal" })} position="top" withArrow>
                        <ActionIcon variant="subtle" color={flipH ? "blue" : "gray"} size="lg" onClick={handleFlipH} aria-label={t("common:imageCrop.flipH", { defaultValue: "Flip horizontal" })} styles={{ root: { color: flipH ? undefined : "white", minWidth: 44, minHeight: 44 } }}>
                          {/* Flip horizontal = mirror left↔right; Tabler draws that as "FlipVertical" (vertical mirror line). */}
                          <AppIcon iconName="FlipVertical" size={20} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common:imageCrop.flipV", { defaultValue: "Flip vertical" })} position="top" withArrow>
                        <ActionIcon variant="subtle" color={flipV ? "blue" : "gray"} size="lg" onClick={handleFlipV} aria-label={t("common:imageCrop.flipV", { defaultValue: "Flip vertical" })} styles={{ root: { color: flipV ? undefined : "white", minWidth: 44, minHeight: 44 } }}>
                          <AppIcon iconName="FlipHorizontal" size={20} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}

                  {showBackgroundRemoval && (
                    <Tooltip
                      label={
                        bgApplied
                          ? t("common:imageCrop.bgRedo", { defaultValue: "Remove background again" })
                          : t("common:imageCrop.bgRemove", { defaultValue: "Remove background" })
                      }
                      position="top"
                      withArrow
                    >
                      <ActionIcon variant="subtle" color={bgApplied ? "blue" : "gray"} size="lg" onClick={handleRemoveBackground} loading={bgBusy} aria-label={t("common:imageCrop.bgRemove", { defaultValue: "Remove background" })} styles={{ root: { color: bgApplied ? undefined : "white", minWidth: 44, minHeight: 44 } }}>
                        <AppIcon iconName="Wand" size={20} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Group>

                {bgError && (
                  <Text c="red.4" size="md" ta="center" mt={6}>
                    {bgError}
                  </Text>
                )}
              </Box>
            )}

            {/* ═══════ Action buttons ═══════ */}
            <Box style={{ position: "relative", zIndex: 10, padding: "10px 24px", paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}>
              <Group justify="center" gap="md">
                {showSkip && onSkip && (
                  <Button variant="subtle" color="gray" size="lg" onClick={handleSkip} disabled={isProcessing || !isReady} styles={{ root: { color: "white", minHeight: 48, paddingLeft: 20, paddingRight: 20 } }} leftSection={<AppIcon iconName="ArrowRight" size={18} />}>
                    {t("common:imageCrop.skip", { defaultValue: "Skip" })}
                  </Button>
                )}

                <Button variant="filled" size="lg" onClick={handleConfirm} disabled={isProcessing || !isReady || bgBusy} loading={isProcessing} leftSection={!isProcessing ? <AppIcon iconName="Check" size={18} /> : undefined} styles={{ root: { minHeight: 48, paddingLeft: 24, paddingRight: 24 } }}>
                  {t("common:imageCrop.done", { defaultValue: "Done" })}
                </Button>
              </Group>
            </Box>
          </Box>
        )}
      </Transition>
    </Portal>
  );
};

export const ImageCropEditor = React.memo(ImageCropEditorComponent);
